// @flow
import FutureJob from "../protocol/FutureJob";
import AssetUploadJob from "./AssetUploadJob";
import UploadScheduler from "./UploadScheduler";
import runAddZmCollectionAssetsQuery from "./runAddZmCollectionAssetsQuery";
import {FuidRrnInfo, ImportQueryResponse, SimpleResponse} from "../autogenerated/ZoomBridge";
import { pushFuidRrnInfoInArray } from '../util/ArrayUtil';

const debug = require('../debug/debug')('wc:upload:CollectionUploadJob');

/**
 * A {@link FutureJob} that manages the process of uploading/importing/adding assets
 * to a collection. The work is divided into two parts:
 *
 * 1) `AssetUploadJob`s are scheduled for upload immediately upon adding them to
 * the `CollectionUploadJob`.
 *
 * 2) Once the `AssetUploadJob`s are complete AND the job has been submitted (by invoking
 * the `submit` method), the add to collection query is executed.
 */
export default class CollectionUploadJob extends FutureJob {
  _collectionId: number;
  _submitted: boolean;
  _didQueueEmpty: boolean;
  _scheduler: UploadScheduler;
  _jobs: AssetUploadJob[];
  _successfulJobs: AssetUploadJob[];

  constructor(collectionId: number) {
    super();

    this._collectionId = collectionId;
    this._submitted = false;
    this._didQueueEmpty = false;
    this._scheduler = new UploadScheduler(this._onQueueEmptied);
    this._jobs = [];
    this._successfulJobs = [];
  }

  get jobs() {
    return this._jobs;
  }

  get successfulJobs() {
    return this._successfulJobs;
  }

  get submitted() {
    return this._submitted;
  }

  get collectionId() {
    return this._collectionId;
  }

  get description() {
    const desc = super.description;
    return {
      label: `Collection upload (${desc.status})`,
      tooltip: `${this._jobs.length} files`,
      status: desc.status
    };
  }

  add(job: AssetUploadJob) {
    debug('[add()] job: ', job);
    if (this._didQueueEmpty) {
      this._didQueueEmpty = false;
    }

    this._jobs.push(job);
    this._scheduler.add(job);
  }

  remove(job: AssetUploadJob) {
    debug('[remove()] job: ', job);
    job.cancel();
    this._jobs = this._jobs.filter((j) => j !== job);
    this._successfulJobs = this._successfulJobs.filter((j) => j !== job);
    this._scheduler.remove(job);
  }

  getFuidRrnList(): FuidRrnInfo[] {
    const fuidRrnInfoArray: FuidRrnInfo[] = [];
    this._successfulJobs.forEach((job: AssetUploadJob) => {
      const res: ImportQueryResponse = job.importQueryResponse;
      debug('[getFuidRrnList()] resp of Import Query', res);
      const fuids: number[] = res.getFuids();
      const rrn = res.getRrn();
      // if zip will get uploaded
      if (fuids.length > 1) {
        fuids.forEach((fuid) =>
          pushFuidRrnInfoInArray(fuid, rrn, fuidRrnInfoArray)
        );
      }
      else {
        pushFuidRrnInfoInArray(fuids[0], rrn, fuidRrnInfoArray);
      }
    });
    return fuidRrnInfoArray;
  }



  async start() {
    try {
      this._started = true;
      debug('[start()] calling runAddZmCollectionAssetsQuery()');
      const res: SimpleResponse = await runAddZmCollectionAssetsQuery(this.getFuidRrnList(), this._collectionId);
      debug('[start()] runAddZmCollectionAssetsQuery success');
      this.emit('resolve', {instance: this, response: res});
    } catch (err) {
      debug('[start()] error: ', err);
      this.emit('reject', {instance: this, error: err});
    }
  }

  submit() {
    debug('[submit()]');
    this._submitted = true;
    this._maybeStartJob();
  }

  cancel() {
    debug('[cancel()]');
    this._canceled = true;
    this._scheduler.clear();
  }

  _maybeStartJob() {
    if (!this._didQueueEmpty) {
      debug('[_maybeStartJob()] !this._didQueueEmpty. return.');
      return;
    }

    if (!this._submitted) {
      debug('[_maybeStartJob()] !this._submitted. return.');
      return;
    }

    if (this._canceled) {
      debug('[_maybeStartJob()] this._canceled. return.');
      return;
    }

    if (this._started) {
      debug('[_maybeStartJob()] this._started. return.');
      return;
    }

    if (!this._successfulJobs.length) {
      debug('[_maybeStartJob()] !this._successfulJobs.length. done with job.');
      this.emit('resolve', {instance: this});
      return;
    }

    this.start();
  }

  _onQueueEmptied = (successfulJobs: AssetUploadJob[]) => {
    debug('[_onQueueEmptied()] successfulJobs: ', successfulJobs);
    this._successfulJobs = this._successfulJobs.concat(successfulJobs);
    this._didQueueEmpty = true;
    this._maybeStartJob();
  };
}
